import React, {createContext, ReactNode, useContext} from "react";
import {UploadItem, UploadResult} from "./types";
import {useUploadQueue} from "./useUploadQueue";
import UploadQueuePanel from "./UploadQueuePanel";

type UploadQueueContextValue = {
    uploads: UploadItem[];
    enqueueUploads: (
        files: File[],
        onCompleted?: (result: UploadResult) => Promise<void> | void,
        onFailed?: (reason: "cancelled" | "error", message?: string) => void
    ) => void;
    cancelUpload: (id: string) => void;
    cancelAllUploads: () => void;
    clearCompletedUploads: () => void;
};

const UploadQueueContext = createContext<UploadQueueContextValue | undefined>(undefined);

export const UploadQueueProvider = ({children}: {children: ReactNode}) => {
    const {
        uploads,
        uploadPanelOpen,
        setUploadPanelOpen,
        enqueueUploads,
        cancelUpload,
        cancelAllUploads,
        clearCompletedUploads
    } = useUploadQueue({maxConcurrent: 4});

    return (
        <UploadQueueContext.Provider value={{
            uploads,
            enqueueUploads,
            cancelUpload,
            cancelAllUploads,
            clearCompletedUploads
        }}>
            {children}
            <UploadQueuePanel
                uploads={uploads}
                uploadPanelOpen={uploadPanelOpen}
                setUploadPanelOpen={setUploadPanelOpen}
                onClearDone={clearCompletedUploads}
                onCancelUpload={cancelUpload}
                onCancelAll={cancelAllUploads}
                onCloseIdle={clearCompletedUploads}
            />
        </UploadQueueContext.Provider>
    );
};

export const useUploadQueueContext = (): UploadQueueContextValue => {
    const ctx = useContext(UploadQueueContext);
    if (!ctx) {
        throw new Error("useUploadQueueContext must be used within an UploadQueueProvider");
    }
    return ctx;
};
